/** @module csi/_base/arrays */
define([
	"dojo",
	"csi/_base/underscore"
], function(dojo, _) {

	/**
	 * Determine if the array or object contains a given string (case insensetive) value (using `===`).
	 * @param {Object[]} obj
	 * @param {string} target
	 * @return {boolean}
	 */
	function _containsStrI(obj, target) {
		if (obj == null) {
			return false;
		}
		if (obj.length !== +obj.length) {
			obj = _.values(obj);
		}
		return _indexOfStrI(obj, target) >= 0;
	};

	/** 
	 * Return the position of the first occurrence of string item in an array (case insensitive),
	 * or -1 if the item is not included in the array.
	 * @param {string[]} arr
	 * @param {string} item
	 * @return {number}
	 */
	function _indexOfStrI(arr, item) {
		if (arr == null) {
			return -1;
		}
		var _item = item.toLowerCase();
		for (var i = 0; i < arr.length; i++) {
			if (arr[i].toLowerCase() === _item) {
				return i;
			}
		}
		return -1;
	};

	/**
	 * Assumption is that this only works with values of same type (number, string, boolean), not with values of various types
	 * @param {Object} obj1
	 * @param {Object} obj2
	 * @return boolean
	 */
	function _sortComparison(obj1, obj2) {
		var a = obj1.y;
		var b = obj2.y;

		if (a === undefined) {
			return -1;
		} else if (b === undefined) {
			return 1;
		}

		return (b < a) - (a < b);
	}

	/**
	 * Sort array of objects by value returned by given function
	 * @param {Object[]} objects
	 * @param {Function} convertObjectToSortValue
	 * @return {Object}
	 */
	function _sortObjects(objects, convertObjectToSortValue) {
		var a = _.map(objects, function(x, i) {
			var y = convertObjectToSortValue(x, i);

			if (y && y.toLowerCase) {
				// sort should be case-insensitive
				y = y.toLowerCase();
			} else if (y == null) {
				// native array sort moves undefined to end of array, so map null to undefined so they are treated the same
				y = undefined;
			}

			return {x, y};
		});

		a.sort(_sortComparison);

		return _.pluck(a, "x");
	}

	/**
	 * Iterate over an array calling function fGroup for each group of items and fItem for each item;
	 * items are separated into groups of length "limit"; if there is more than one group, then use timeout to separate execution
	 * @param {Any[]} a
	 * @param {number} limit
	 * @param {Function} fGroup
	 * @param {Function} fItem
	 * @param {Function} fEnd
	 * @param {Object} scope
	 * @return {RemoveEventHandle | undefined} - returns object with remove method to clear all timeouts
	 */
	function _forEachWithTimeout(a, limit, fGroup, fItem, fEnd, scope) {
		const handles = [];
		const n = a?.length ?? 0;

		if (!n) {
			fEnd?.call(scope);
		} else {
			var i = 0;

			var iterate = function() {
				var group = a.slice(i, i + limit);

				if (fGroup) {
					fGroup.call(scope, group, i);
				}

				if (fItem) {
					_.each(group, fItem, scope);
				}

				i += limit;

				if (i < n) {
					handles.push(setTimeout(iterate, 1));
				} else if (fEnd) {
					fEnd.call(scope);
				}
			};

			scope = scope || dojo.global;
			iterate();
		}

		return {
			remove: () => {
				handles.forEach((handle) => clearTimeout(handle));
			}
		};
	}

	/**
	 * Forward to array function
	 * @param {Any[]} a
	 * @param {Function|Any} match
	 * @param {number} start
	 * @return {number}
	 */
	function _locate(a, match, start) {
		if (!a) return -1;

		var n = a.length;
		var f;

		start = start || 0;

		if (typeof (match) == 'function') {
			for (f = start; f < n; ++f) {
				if (match(a[f], f)) {
					return f;
				}
			}
		} else if (match) {
			for (f = start; f < n; ++f) {
				if (a[f] == match) {
					return f;
				}
			}
		}

		return -1;
	}

	/**
	 * Forward to array function
	 * @param {Any[]} a
	 * @param {Function|Any} match
	 * @param {number} start
	 * @param {boolean} bAll
	 * @return {Any[]}
	 */
	function _search(a, match, start, bAll) {
		if (!a) return a;

		var results = null;
		var found = 0;

		start = start || 0;

		while (found != -1) {
			found = _locate(a, match, start);

			if (found > -1) {
				if (!bAll) return a[found];

				results = results || [];
				results.push(a[found]);
				start = found + 1;
			}
		}

		return results;
	}

	/**
	 * Sort array of objects
	 * @param {Any[]} a - array to sort
	 * @param {string|Function} [param] - if string, then sort array based on some member of objects in array; 
	 * 		if function, then sort based on result of function
	 * 		if not defined, then sort using default rules, which includes a case-insensitive sort
	 * @return {Object}
	 */
	function _sort(a, param) {
		if (!a || !a.length) {
			return a;
		}

		param = param || _.identity;

		return _sortObjects(a, (typeof param === "string") ? function(x, i) {return x[param];} : param);
	}

	/**
	 * Sort given array "a" by corresponding values in second array "sortArray"
	 * @param {Any[]} a
	 * @param {Any[]} sortArray
	 * @return {Any[]}
	 */
	function _sortByArray(a, sortArray) {
		if (!a || !a.length) {
			return a;
		}

		console.assert(!!sortArray && (sortArray.length == a.length));

		return _sortObjects(a, function(x, i) {return sortArray[i];});
	}

	/**
	 * Same as _.filter(_.map(a, f), function(x) { return x != null; }, scope)
	 * @param {Any[]} a
	 * @param {Function} f
	 * @param {Any} [scope]
	 * @return {Any[]|null}
	 */
	function _transform(a, f, scope) {
		if (!a) return null;

		var aResults = [];
		var n = a.length;
		var obj;

		scope = scope || dojo.global;

		for (var i = 0; i < n; ++i) {
			obj = f.call(scope, a[i], i);

			if (obj != null) {
				aResults.push(obj);
			}
		}

		return aResults.length ? aResults : null;
	}

	/**
	 * Return first non-null result of function
	 * @param {Any[]} a
	 * @param {Function} f
	 * @param {Any} [scope]
	 */
	function _transformOne(a, f, scope) {
		if (!a) return null;

		var n = a.length;
		var obj;

		scope = scope || dojo.global;

		for (var i = 0; i < n; ++i) {
			if (obj = f.call(scope, a[i], i)) {
				return obj;
			}
		}

		return null;
	}

	if (!Array.prototype.first) {
		// DEPRECATE: Use _.first(a)
		Array.prototype.first = function() {
			return this.length ? this[0] : null;
		};
	}

	if (!Array.prototype.last) {
		// DEPRECATE: Use _.last(a)
		Array.prototype.last = function() {
			return this.length ? this[this.length - 1] : null;
		};
	}

	if (!Array.prototype.dele) {
		// DEPRECATE: Use _.without(a)
		Array.prototype.dele = function(match) {
			var targetIndex = _locate(this, match);
			if (targetIndex >= 0) {
				var obj = this[targetIndex];
				this.splice(targetIndex, 1);
				return obj;
			}

			return null;
		};
	}

	return {
		containsStrI: _containsStrI,
		forEachWithTimeout: _forEachWithTimeout,
		indexOfStrI: _indexOfStrI,
		locate: _locate,
		search: _search,
		sort: _sort,
		sortByArray: _sortByArray,
		transform: _transform,
		transformOne: _transformOne
	};
});